import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { WIDGET_SPACING_CLASS, type WidgetSpacing } from '../spacing';
import type { DividerColor, HorizontalDividerResolved } from './types';

/**
 * Editor-facing colour name to the theme token that paints the line.
 *
 * `grey` is `--color-surface-muted` (#eae8e7), the neutral the legacy sites
 * drew their `<hr>` in, and `dark` is `--color-secondary` (#28190a), the body
 * text colour — the two ends of the scale. Every value is a literal class so
 * Tailwind's scanner sees it; none may be built by interpolation.
 */
const COLOR_CLASS: Record<DividerColor, string> = {
  'grey': 'border-surface-muted',
  'light-blue': 'border-light-blue',
  'primary': 'border-primary',
  'secondary-light': 'border-secondary-light',
  'dark': 'border-secondary',
};

export const DEFAULT_DIVIDER_COLOR: DividerColor = 'grey';

/**
 * Mirrors this widget's own Strapi default, which is `m` (24px) rather than
 * the shared `DEFAULT_WIDGET_SPACING` of `l`: the divider replaces the legacy
 * `hr { margin-top: 24px }`, and `m` is that value.
 */
export const DEFAULT_DIVIDER_SPACING: WidgetSpacing = 'm';

/**
 * A horizontal rule between two blocks.
 *
 * Two elements, per the Widget Spacing convention (70.12): the outer `<div>`
 * carries the inter-widget rhythm as top padding, the inner `<hr>` draws the
 * line. Putting the padding on the `<hr>` itself would place the line above
 * its own padding — `border-top` sits at the outer edge of the padding box —
 * so the gap would land under the line instead of above it.
 *
 * There is deliberately no bottom padding: the gap after a widget belongs to
 * whatever comes next, which brings at least its own `pt-4`.
 *
 * The line needs no width of its own: it sits in the same wrapper-supplied
 * content column as the text widgets it separates (70.14-Widget-Width), so it
 * ends where their text ends rather than bleeding to the viewport edge.
 */
export const HorizontalDividerComponent = ({
  resolved,
}: {
  resolved: HorizontalDividerResolved;
}) => {
  const { color, spacing } = resolved;

  // `WIDGET_SPACING_CLASS['0']` is an empty string on purpose, so test for the
  // key rather than for a truthy value — `||` would turn `0` back into `m`.
  const spacingClass =
    spacing in WIDGET_SPACING_CLASS
      ? WIDGET_SPACING_CLASS[spacing]
      : WIDGET_SPACING_CLASS[DEFAULT_DIVIDER_SPACING];

  const colorClass = COLOR_CLASS[color] ?? COLOR_CLASS[DEFAULT_DIVIDER_COLOR];

  return (
    <div className={classNames('', spacingClass)}>
      {/* `border-t` only: Tailwind's preflight already zeroes every border
          and gives `hr` a 1px top border, so an added `border-0` would be
          redundant and would depend on it sorting before `border-t`. */}
      <hr className={classNames('border-t', colorClass)} />
    </div>
  );
};
